import React, { useEffect, useState } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { motion } from "framer-motion";

import { useAuth } from "../Auth/AuthContext";
import AccountDetailApiService from "../../ApiServices/SettingApiServices/AccountDetailApiService";

const AddAccountDetailForm = ({ isOpen, closeModal }) => {
  const [formData, setFormData] = useState({
    FirstName: "",
    LastName: "",
    Company: "",
    PhoneNumber: "",
  });
  const [loading, setLoading] = useState(false);
  const Auth = useAuth();
  const user = Auth.authState.user;

  // Initialize AOS and fetch data
  useEffect(() => {
    AOS.init({ duration: 800, once: true });
    if (isOpen || !user) return;
    fetchData();
  }, [isOpen]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const UserId = user?.id;
      if (!UserId) throw new Error("User ID is undefined");

      const settingData = await AccountDetailApiService.Get(UserId);
      setFormData({
        FirstName: settingData.data.firstName || "",
        LastName: settingData.data.lastName || "",
        Company: settingData.data.company || "",
        PhoneNumber: settingData.data.phoneNumber || "",
      });
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load user details.");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const UserId = user?.id;
    setLoading(true);

    try {
      const accountDetailData = {
        ...formData,
        Id: UserId,
      };

      const result = await AccountDetailApiService.Update(accountDetailData);
      toast.success(result.message);
    } catch (error) {
      console.error("Error updating data:", error);
      toast.error("Failed to update user details.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <ToastContainer />
      <motion.div
        className="bg-white p-6 rounded-2xl shadow-xl max-w-xl mx-auto"
        data-aos="fade-up"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.5 }}
      >
        <h2 className="text-2xl font-semibold text-gray-800 mb-6">User Details</h2>

        <form onSubmit={handleSubmit} className="space-y-5">
          {["FirstName", "LastName", "Company", "PhoneNumber"].map((field) => (
            <div key={field}>
              <label
                htmlFor={field}
                className="block text-sm font-medium text-gray-600 mb-1"
              >
                {field}
              </label>
              <input
                type="text"
                id={field}
                name={field}
                value={formData[field]}
                onChange={handleInputChange}
                disabled={loading}
                placeholder={`Enter ${field}`}
                className="w-full border border-gray-300 rounded-md px-4 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>
          ))}

          <div className="flex justify-start gap-4 pt-2">
            <button
              type="submit"
              disabled={loading}
              className={`px-5 py-2.5 rounded-md text-white text-sm font-medium transition-all duration-300 
                ${loading ? "bg-blue-300" : "bg-blue-600 hover:bg-blue-700 shadow-md"}`}
            >
              {loading ? "Saving..." : "Save Changes"}
            </button>

            {closeModal && (
              <button
                type="button"
                onClick={closeModal}
                className="px-5 py-2.5 rounded-md text-gray-600 text-sm bg-gray-100 hover:bg-gray-200 border border-gray-300"
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      </motion.div>
    </>
  );
};

export default AddAccountDetailForm;
